import { Link } from '@tanstack/react-router';

import type { CategoryNavigationItem } from '#/components/layout/CategoryNavigation';
import {
  CategoryNavigationCta,
  CategoryNavigationItemsList,
} from '#/components/layout/CategoryNavigation/ItemLists';
import { Icon } from '#/components/ui/icons';
import { cn } from '#/utils/cn';
import { APP_NAME } from '#/utils/constants';

import type { HomepageDesktopTabId } from './useDesktopHomepageNavigation';
import { useDesktopHomepageNavigation } from './useDesktopHomepageNavigation';

interface DesktopHomepageNavigationProps {
  productTypes: CategoryNavigationItem[];
  sectors: CategoryNavigationItem[];
  cta?: CategoryNavigationItem;
}

function DesktopNavigationTrigger({
  label,
  tab,
  activeTab,
  overlayOpen,
  onSelect,
  buttonRef,
}: {
  label: string;
  tab: HomepageDesktopTabId;
  activeTab: HomepageDesktopTabId;
  overlayOpen: boolean;
  onSelect: (tab: HomepageDesktopTabId) => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const isActive = overlayOpen && activeTab === tab;

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-expanded={isActive}
      aria-controls="homepage-navigation-overlay"
      onClick={() => onSelect(tab)}
      className={cn(
        'text-static-16 flex items-center gap-8 border-b-4 border-transparent py-8 leading-normal transition-colors',
        isActive ? 'text-fg-header border-blue-20' : 'text-slate-60',
      )}
    >
      <span
        data-label={label}
        className="grid after:invisible after:col-start-1 after:row-start-1 after:block after:font-semibold after:content-[attr(data-label)]"
      >
        <span
          className={cn(
            'col-start-1 row-start-1',
            isActive ? 'font-semibold' : 'font-normal',
          )}
        >
          {label}
        </span>
      </span>
      <Icon
        id="chevron-left"
        size={14}
        className={cn(
          'rotate-90 transition-transform',
          isActive ? '-rotate-90!' : undefined,
        )}
      />
    </button>
  );
}

export function DesktopHomepageNavigation({
  productTypes,
  sectors,
  cta,
}: DesktopHomepageNavigationProps) {
  const {
    shellRef,
    productButtonRef,
    sectorButtonRef,
    overlayOpen,
    activeTab,
    floatingStyles,
    handleTriggerSelect,
    setFloatingRef,
  } = useDesktopHomepageNavigation();

  return (
    <div className="relative hidden lg:block">
      <nav aria-label="Homepage navigation" className="relative z-10">
        <div className="flex items-center gap-28 py-8">
          <Link
            to="/"
            className="text-static-16 text-fg-header inline-flex min-h-40 items-center font-semibold"
          >
            {APP_NAME}
          </Link>
          <div ref={shellRef} className="flex items-center gap-24">
            <DesktopNavigationTrigger
              label="Investment Products"
              tab="productTypes"
              activeTab={activeTab}
              overlayOpen={overlayOpen}
              onSelect={handleTriggerSelect}
              buttonRef={productButtonRef}
            />
            <DesktopNavigationTrigger
              label="Sectors"
              tab="sectors"
              activeTab={activeTab}
              overlayOpen={overlayOpen}
              onSelect={handleTriggerSelect}
              buttonRef={sectorButtonRef}
            />
          </div>
        </div>
      </nav>

      <div
        ref={setFloatingRef}
        id="homepage-navigation-overlay"
        style={floatingStyles}
        aria-hidden={overlayOpen ? undefined : true}
        className={cn(
          'bg-gray-10 z-20 w-fit max-w-[calc(100vw-2rem)] rounded-lg border border-(--color-slate-30) p-12 shadow-md transition-[opacity,transform,visibility] duration-150 ease-[cubic-bezier(0.2,0,0,1)]',
          overlayOpen
            ? 'visible translate-y-0 opacity-100'
            : 'pointer-events-none invisible -translate-y-2 opacity-0',
        )}
      >
        {activeTab === 'productTypes' ? (
          <CategoryNavigationItemsList
            items={productTypes}
            variant="desktopCards"
            columnsClass="grid-cols-1"
            extraItem={<CategoryNavigationCta cta={cta} />}
          />
        ) : (
          <CategoryNavigationItemsList
            items={sectors}
            variant="desktopLinks"
            columnsClass="grid-cols-1"
          />
        )}
      </div>
    </div>
  );
}
